import { useEffect, useMemo, useState } from "react";

export default function Highlights({
  productId,
  value = [],
  onChange,
  onSaved,
  onError,
  apiBase = process.env.REACT_APP_API_URL,
  method = "PUT",
  extraPayload = {},
  headers = {},
}) {
  // Initialize with objects { heading: "", description: "" }
  const [items, setItems] = useState(
    value.length ? value : [{ heading: "", description: "" }]
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // compute endpoint once
  const endpoint = useMemo(() => {
    if (!apiBase) return null;
    return `${apiBase.replace(/\/+$/, "")}/api/products/edit/${productId}`;
  }, [apiBase, productId]);

  // sync from parent value - handle both old string format and new object format
  useEffect(() => {
    if (!value || value.length === 0) {
      setItems([{ heading: "", description: "" }]);
      return;
    }

    // Convert string array to object array for backward compatibility
    const normalized = value.map((item) => {
      if (typeof item === "string") {
        return { heading: item, description: "" };
      }
      return {
        heading: item.heading || "",
        description: item.description || "",
      };
    });

    setItems(normalized);
  }, [value]);

  const updateItem = (idx, field, newVal) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: newVal };
    setItems(next);
    onChange?.(next);
  };

  const addItem = () => {
    const next = [...items, { heading: "", description: "" }];
    setItems(next);
    onChange?.(next);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    const finalList = next.length ? next : [{ heading: "", description: "" }];
    setItems(finalList);
    onChange?.(finalList);
  };

  const handleKeyDown = (e, idx, field) => {
    // Enter on description field or last heading adds a new item
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if ((field === "description" || idx === items.length - 1)) {
        addItem();
      }
    }
    // Ctrl/Cmd+Enter triggers save
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  const cleanedHighlights = () =>
    items
      .map((item) => ({
        heading: (item.heading ?? "").trim(),
        description: (item.description ?? "").trim(),
      }))
      .filter((item) => item.heading); // Only keep items with heading

  const handleSave = async () => {
    if (!productId) {
      setMessage("❌ No productId provided");
      onError?.(new Error("No productId provided"));
      return;
    }
    if (!endpoint) {
      setMessage("❌ API base URL not set (REACT_APP_API_URL missing?)");
      onError?.(new Error("API base URL missing"));
      return;
    }

    const highlights = cleanedHighlights();

    const body =
      method.toUpperCase() === "PUT"
        ? { ...extraPayload, highlights }
        : { highlights, ...extraPayload };

    try {
      setLoading(true);
      setMessage("");

      const res = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Failed to save highlights (HTTP ${res.status})`;
        throw new Error(msg);
      }

      setMessage("✅ Highlights saved successfully");
      onSaved?.(data);

      // Refresh items from server response
      if (data?.product?.highlights && Array.isArray(data.product.highlights)) {
        const normalized = data.product.highlights.map((item) => ({
          heading: item.heading || "",
          description: item.description || "",
        }));
        setItems(normalized.length ? normalized : [{ heading: "", description: "" }]);
        onChange?.(data.product.highlights);
      }
    } catch (err) {
      const msg = err?.message || "Failed to save highlights";
      setMessage("❌ " + msg);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card my-5">
        <div className="card-header d-flex align-items-center justify-content-between">
          <label className="form-label m-0">HIGHLIGHTS</label>
          <small className="text-muted">Enter → add row · Ctrl/Cmd+Enter → save</small>
        </div>

        <div className="card-body">
          {items.map((item, idx) => (
            <div className="mb-3" key={idx}>
              <div className="d-flex gap-2 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Heading (e.g., "8 GB RAM")`}
                  value={item.heading}
                  onChange={(e) => updateItem(idx, "heading", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, idx, "heading")}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => removeItem(idx)}
                  disabled={loading}
                  aria-label={`Remove highlight ${idx + 1}`}
                  title="Remove"
                >
                  Remove
                </button>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder={`Description (e.g., "RAM + Virtual RAM Expandable")`}
                value={item.description}
                onChange={(e) => updateItem(idx, "description", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, idx, "description")}
                disabled={loading}
              />
            </div>
          ))}

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={addItem}
              disabled={loading}
            >
              Add new
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>

          {message && <p className="mt-3 mb-0">{message}</p>}
        </div>
      </div>
    </>
  );
}